<div class="container-fluid complyon-page vault-page px-3 px-md-4 px-xl-4 py-3">
  <div class="complyon-shell vault-shell mx-auto">
    <section class="mb-3">
      <h1 class="complyon-title vault-title mb-2 text-l">Evidence vault</h1>
      <p class="complyon-subtitle vault-subtitle mb-0 text-s">
        Searchable proof layer linking documents to workflows, controls, risks, audits, suppliers and people.
      </p>
    </section>

    <section class="vault-tabbar mb-3">
      <div class="vault-tabs-wrap">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="vault-tab"
          :class="{ 'vault-tab-active': active_tab === tab.key }"
          @click="set_tab(tab.key)"
        >
          <span>{{ tab.label }}</span>
          <span v-if="tab.count !== null" class="vault-tab-count">{{ tab.count }}</span>
        </button>
      </div>

      <button type="button" class="vault-reset btn btn-link text-decoration-none p-0">
        <i class="bi bi-arrow-clockwise"></i>
        <span>Reset sample</span>
      </button>
    </section>

    <div v-if="active_tab === 'repository'">
      <section class="vault-summary-grid mb-3">
        <div v-for="stat in repository_stats" :key="stat.label" class="complyon-card vault-stat-card">
          <div class="vault-stat-label text-uppercase">{{ stat.label }}</div>
          <div class="vault-stat-value" :class="`vault-tone-${stat.tone}`">{{ stat.value }}</div>
          <div class="vault-stat-note">{{ stat.note }}</div>
        </div>
      </section>

      <section class="vault-toolbar mb-2">
        <div class="input-group vault-search">
          <span class="input-group-text bg-white"><i class="bi bi-search text-secondary"></i></span>
          <input
            type="search"
            class="form-control"
            placeholder="Search by name, tag, linked record..."
            aria-label="Search evidence"
          >
        </div>

        <button type="button" class="btn btn-primary-dark d-inline-flex align-items-center gap-2 px-3 py-2 rounded-3 fw-semibold">
          <i class="bi bi-upload"></i>
          <span>Upload evidence</span>
        </button>
      </section>

      <section class="vault-chipbar mb-3">
        <button
          v-for="category in repository_categories"
          :key="category.label"
          type="button"
          class="vault-chip"
          :class="chip_class(category.active)"
        >
          <i class="bi" :class="category_icon(category.label)"></i>
          <span>{{ category.label }}</span>
          <span class="vault-chip-count">{{ category.count }}</span>
        </button>
      </section>

      <section class="complyon-panel vault-panel rounded-4 overflow-hidden">
        <div class="table-responsive">
          <table class="table complyon-table vault-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Ref</th>
                <th class="text-xs">Evidence</th>
                <th class="text-xs">Type</th>
                <th class="text-xs">Linked to</th>
                <th class="text-xs">Access</th>
                <th class="text-xs">Ver</th>
                <th class="text-xs">Status</th>
                <th class="vault-table-chevron"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in repository_rows" :key="row.ref">
                <td class="ps-3 vault-ref">{{ row.ref }}</td>
                <td>
                  <div class="vault-evidence-name">{{ row.evidence }}</div>
                  <div class="vault-evidence-meta">{{ row.meta }}</div>
                </td>
                <td>{{ row.type }}</td>
                <td class="vault-linked">{{ row.linked_to }}</td>
                <td>
                  <span class="badge complyon-badge vault-badge" :class="access_class(row.access)">{{ row.access }}</span>
                </td>
                <td class="vault-ver">{{ row.ver }}</td>
                <td>
                  <span class="badge complyon-badge vault-badge" :class="badge_class(row.status_tone)">{{ row.status }}</span>
                </td>
                <td class="vault-table-chevron text-end"><i class="bi bi-chevron-right"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'audit_folders'" class="vault-folder-grid">
      <div v-for="card in audit_folder_cards" :key="card.title" class="complyon-card vault-folder-card">
        <div class="vault-folder-head">
          <span class="vault-folder-icon"><i class="bi bi-folder2"></i></span>
          <span class="badge complyon-badge vault-folder-status" :class="folder_status_class(card.status_tone)">{{ card.status }}</span>
        </div>

        <h3 class="vault-folder-title mb-1">{{ card.title }}</h3>
        <p class="vault-folder-subtitle mb-3">{{ card.subtitle }}</p>

        <div class="vault-folder-progress">
          <div class="vault-folder-progress-track">
            <span class="vault-folder-progress-fill" :style="completion_bar_style(card.progress)"></span>
          </div>
          <div class="vault-folder-progress-meta">
            <span>{{ card.progress_label }}</span>
            <span>{{ card.valid_label }}</span>
          </div>
        </div>

        <div class="vault-folder-footer">
          <div class="vault-folder-owner">{{ card.owner }}</div>
          <div class="vault-folder-actions">
            <button type="button" class="btn btn-link text-decoration-none p-0">Open</button>
            <button type="button" class="btn btn-outline-secondary d-inline-flex align-items-center gap-2 px-3 py-1 rounded-3">
              <i class="bi bi-download"></i>
              <span>Pack</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="active_tab === 'evidence_requests'">
      <section class="vault-summary-grid mb-3">
        <div v-for="stat in evidence_request_stats" :key="stat.label" class="complyon-card vault-stat-card">
          <div class="vault-stat-label text-uppercase">{{ stat.label }}</div>
          <div class="vault-stat-value" :class="`vault-tone-${stat.tone}`">{{ stat.value }}</div>
          <div class="vault-stat-note">{{ stat.note }}</div>
        </div>
      </section>

      <section class="complyon-panel vault-panel rounded-4 overflow-hidden">
        <div class="vault-panel-head">
          <div>
            <h2 class="vault-section-title mb-1">Evidence requests (RFI)</h2>
            <p class="vault-section-subtitle mb-0">Requests for information from auditors and reviewers - what's needed, by whom and when.</p>
          </div>
          <button type="button" class="btn btn-primary-dark d-inline-flex align-items-center gap-2 px-3 py-2 rounded-3 fw-semibold">
            <i class="bi bi-plus-lg"></i>
            <span>New request</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table vault-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Ref</th>
                <th class="text-xs">Evidence requested</th>
                <th class="text-xs">Requested by</th>
                <th class="text-xs">Owner</th>
                <th class="text-xs">Due</th>
                <th class="text-xs">Status</th>
                <th class="vault-table-chevron"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in evidence_request_rows" :key="row.ref">
                <td class="ps-3 vault-ref">{{ row.ref }}</td>
                <td class="vault-evidence-name">{{ row.evidence }}</td>
                <td>{{ row.requested_by }}</td>
                <td>{{ row.owner }}</td>
                <td>{{ row.due }}</td>
                <td>
                  <span class="badge complyon-badge vault-badge" :class="badge_class(row.status_tone)">{{ row.status }}</span>
                </td>
                <td class="vault-table-chevron text-end"><i class="bi bi-chevron-right"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else>
      <section class="vault-completeness-score complyon-panel rounded-4 overflow-hidden mb-3">
        <div class="vault-completeness-ring" :style="ring_style(completeness_summary.score)">
          <div class="vault-completeness-ring-inner">
            <div class="vault-completeness-ring-value">{{ completeness_summary.score }}%</div>
            <div class="vault-completeness-ring-label">COMPLETE</div>
          </div>
        </div>

        <div class="vault-completeness-copy">
          <h2 class="vault-section-title mb-1">Evidence completeness score</h2>
          <p class="vault-section-subtitle mb-3">
            How much of the evidence the organisation should hold is actually on file and still valid - measured across every domain.
          </p>
          <div class="vault-completeness-metrics">
            <div>
              <div class="vault-completeness-metric-value vault-tone-success">{{ completeness_summary.valid_on_file }}</div>
              <div class="vault-completeness-metric-label">Valid on file</div>
            </div>
            <div>
              <div class="vault-completeness-metric-value">{{ completeness_summary.required }}</div>
              <div class="vault-completeness-metric-label">Required</div>
            </div>
            <div>
              <div class="vault-completeness-metric-value vault-tone-danger">{{ completeness_summary.gaps }}</div>
              <div class="vault-completeness-metric-label">Gaps</div>
            </div>
          </div>
        </div>
      </section>

      <section class="complyon-panel vault-panel rounded-4 overflow-hidden">
        <div class="vault-panel-head">
          <div>
            <h2 class="vault-section-title mb-1">Completeness by domain</h2>
            <p class="vault-section-subtitle mb-0">Required vs on-file vs valid for every record type that must be backed by evidence.</p>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table vault-table vault-completeness-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Domain</th>
                <th class="text-xs">Required</th>
                <th class="text-xs">On file</th>
                <th class="text-xs">Valid</th>
                <th class="text-xs">Completeness</th>
                <th class="vault-table-chevron"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in completeness_rows" :key="row.domain">
                <td class="ps-3 vault-evidence-name">{{ row.domain }}</td>
                <td>{{ row.required }}</td>
                <td>{{ row.on_file }}</td>
                <td>{{ row.valid }}</td>
                <td>
                  <div class="vault-completeness-cell">
                    <div class="vault-completeness-track">
                      <span class="vault-completeness-fill" :class="`vault-tone-${row.tone}`" :style="completion_bar_style(row.completion)"></span>
                    </div>
                    <span class="vault-completeness-percent">{{ row.completion }}%</span>
                  </div>
                </td>
                <td class="vault-table-chevron text-end"><i class="bi bi-chevron-right"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </div>
</div>
